import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';

import * as StoreService from '../../api/storeService';
import * as ProfileService from '../../api/profileService';
import * as EmploymentService from '../../api/employmentService';
import * as RestrictionService from '../../api/restrictionService';
import * as StampCampaignService from '../../api/stampCampaignService';
import useAuthContext from '../../hooks/useAuthContext';
import useNavContext from '../../hooks/useNavContext';
import useSelectedStoreContext from '../../hooks/useSelectedStoreContext';
import QRCodeOnlyScreen from '../../components/Screen/QRCodeOnlyScreen';
import HowToUseAppDialog from '../../components/Dialog/HowToUseAppDialog';
import SaveToHomeScreenDialog from '../../components/Dialog/SaveToHomeScreenDialog';
import SelectStoreDialog from '../../components/Dialog/SelectStoreDialog';
import Spinner from '../../components/Spinner';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  grow: {
    flexGrow: 1,
  },
}));

/**
 * QR Code Mode screen
 */
export default function QRCodeMode() {
  const classes = useStyles();
  const selectedStore = useSelectedStoreContext();
  const [howToUseAppDialog, setHowToUseAppDialog] = useState(false);
  const [saveToHomeDialog, setSaveToHomeDialog] = useState(false);
  const [selectStoreDialogOpen, setSelectStoreDialogOpen] = useState(
    selectedStore && !selectedStore.selectedStore?.storeId ? true : false
  );
  const [profile, setProfile] = useState<ProfileService.Profile | null>(null);
  const [employment, setEmployment] =
    useState<EmploymentService.Employment | null>(null);
  const [restrictedStamp, setRestrictedStamp] = useState(1);
  const [maxStampIssued, setMaxStampIssued] = useState(1);
  const [stampCampaignList, setStampCampaignList] = useState<
    StampCampaignService.StampCampaign[]
  >([]);
  const [init, setInit] = useState(true);
  const auth = useAuthContext();
  const nav = useNavContext();
  const history = useHistory();

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await ProfileService.get();
        setProfile(res);
      } catch (e) {
        console.log('Error: ', e);
      }
    };

    fetch();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await EmploymentService.get();
        if (res) {
          setEmployment(res);
        }
      } catch (e) {
        console.log('Error:', e);
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    const fetchRestrictionList = async () => {
      if (employment) {
        try {
          const res = await RestrictionService.listEmployeeRestriction(
            employment.company.id,
            employment.id
          );
          const resStampCampaignList = await StampCampaignService.listActive(
            employment.company.id
          );
          if (res && resStampCampaignList) {
            setStampCampaignList(resStampCampaignList);
            const restrictionStampScan = res.find(
              (x) => x.type === 'stamps_per_scan'
            );
            const restrictionStampLimit = res.find(
              (x) => x.type === 'stamp_scan_limit'
            );

            if (employment.role !== 'restricted' && restrictionStampLimit) {
              setMaxStampIssued(
                parseInt(restrictionStampLimit.restriction[0], 10)
              );
            } else {
              setMaxStampIssued(20);
            }

            if (employment.role === 'restricted' && restrictionStampScan) {
              setRestrictedStamp(
                parseInt(restrictionStampScan.restriction[0], 10)
              );
            }

            setInit(false);
          }
        } catch (e) {
          console.log('Error:', e);
          setInit(false);
        }
      }
    };
    if (employment && init) {
      fetchRestrictionList();
    }
  }, [employment, init]);

  const handleLogOut = async () => {
    auth?.removeSession();
    nav?.removeNav();
    selectedStore?.removeSelectedStore();
    history.replace({});
    history.push(`/`);
  };

  if (!profile) {
    return null;
  }

  if (!employment) {
    return null;
  }

  if (stampCampaignList.length === 0) {
    return null;
  }

  if (init) {
    return <Spinner />;
  }

  return (
    <>
      <div className={classes.grow}>
        <div className={classes.container}>
          {selectedStore &&
            selectedStore.selectedStore &&
            selectedStore.selectedStore.storeId &&
            profile && (
              <QRCodeOnlyScreen
                onLogout={() => {
                  handleLogOut();
                }}
                stampNum={profile.role === 'restricted' ? restrictedStamp : 1}
                storeId={selectedStore.selectedStore.storeId}
                onlyScanOnce={profile.role === 'restricted' ? true : false}
                maxStampIssued={maxStampIssued}
                stampCampaignList={stampCampaignList}
              />
            )}
          <SelectStoreDialog
            employment={employment}
            profile={profile}
            open={selectStoreDialogOpen}
            onClose={() => setSelectStoreDialogOpen(false)}
            onSuccess={(store: StoreService.Store) => {
              selectedStore?.setSelectedStore({
                storeId: store.id,
              });
              setHowToUseAppDialog(true);
            }}
          />
          <HowToUseAppDialog
            open={howToUseAppDialog}
            onClose={() => setHowToUseAppDialog(false)}
            onSuccess={() => {
              setHowToUseAppDialog(false);
              setSaveToHomeDialog(true);
            }}
          />
          <SaveToHomeScreenDialog
            open={saveToHomeDialog}
            onClose={() => setSaveToHomeDialog(false)}
          />
        </div>
      </div>
    </>
  );
}
